import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { StatusBadge } from '../components/StatusBadge';
import { PolicySummary } from '../components/PolicySummary';
import { AuditTimeline, AuditEvent } from '../components/AuditTimeline';
import { CopyButton } from '../components/CopyButton';
import { ArrowLeftIcon, EditIcon, FileTextIcon, PauseIcon, ActivityIcon, ZapIcon, ShieldCheckIcon } from 'lucide-react';
const mockServer = {
  id: 'srv_8x7k2m9p',
  name: 'Production API Gateway',
  endpoint: 'https://mcp.mcpguard.io/v1/gateway',
  status: 'healthy' as const,
  uptime: '99.97%',
  avgLatency: '45ms',
  policyMode: 'Default deny',
  allowedTools: 12,
  lastActivity: '2 min ago'
};
const mockPolicyRules = [{
  type: 'allow' as const,
  description: 'Allow tools/list and tools/call methods'
}, {
  type: 'allow' as const,
  description: 'Allow 12 specific tools from allowlist'
}, {
  type: 'deny' as const,
  description: 'Deny all network egress'
}, {
  type: 'limit' as const,
  description: 'Request body limited to 1MB'
}, {
  type: 'limit' as const,
  description: 'Response body limited to 10MB'
}];
const mockRecentEvents: AuditEvent[] = [{
  id: '1',
  timestamp: '2 min ago',
  direction: 'ingress',
  method: 'tools/call',
  decision: 'allowed',
  latency: 42
}, {
  id: '2',
  timestamp: '5 min ago',
  direction: 'ingress',
  method: 'tools/list',
  decision: 'allowed',
  latency: 12
}, {
  id: '3',
  timestamp: '8 min ago',
  direction: 'egress',
  method: 'http/request',
  decision: 'denied',
  reason: 'Network egress blocked by policy'
}, {
  id: '4',
  timestamp: '12 min ago',
  direction: 'ingress',
  method: 'resources/read',
  decision: 'denied',
  reason: 'Method not in allowlist'
}, {
  id: '5',
  timestamp: '15 min ago',
  direction: 'ingress',
  method: 'tools/call',
  decision: 'allowed',
  latency: 38
}];
const tabs = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'policy',
  label: 'Policy'
}, {
  id: 'audit',
  label: 'Audit',
  count: 156
}, {
  id: 'runtime',
  label: 'Runtime'
}];
export function ServerDetail() {
  const {
    serverId
  } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  return <div data-id="element-359">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-6" data-id="element-360">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-361" />
        Back to Servers
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6" data-id="element-362">
        <div data-id="element-363">
          <div className="flex items-center gap-3" data-id="element-364">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-365">
              {mockServer.name}
            </h1>
            <StatusBadge status={mockServer.status} data-id="element-366" />
          </div>
          <div className="flex items-center gap-1 mt-1" data-id="element-367">
            <span className="text-sm font-mono text-slate-500 dark:text-slate-400" data-id="element-368">
              {mockServer.id}
            </span>
            <CopyButton value={mockServer.id} data-id="element-369" />
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-370">
          <Button variant="secondary" size="sm" data-id="element-371">
            <PauseIcon className="h-4 w-4 mr-1.5" data-id="element-372" />
            Pause Server
          </Button>
          <Link to={`/servers/${serverId}/policy`} data-id="element-373">
            <Button variant="secondary" size="sm" data-id="element-374">
              <EditIcon className="h-4 w-4 mr-1.5" data-id="element-375" />
              Edit Policy
            </Button>
          </Link>
          <Link to={`/audit?server=${serverId}`} data-id="element-376">
            <Button variant="primary" size="sm" data-id="element-377">
              <FileTextIcon className="h-4 w-4 mr-1.5" data-id="element-378" />
              View Audit
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-379" />

      {/* Tab Content */}
      <div className="mt-6" data-id="element-380">
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-381">
            {/* Metrics */}
            <div className="lg:col-span-2 space-y-6" data-id="element-382">
              {/* Health Metrics */}
              <Card data-id="element-383">
                <CardHeader title="Health Metrics" data-id="element-384" />
                <div className="grid grid-cols-3 gap-4" data-id="element-385">
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg" data-id="element-386">
                    <ActivityIcon className="h-5 w-5 text-slate-400 dark:text-slate-500 mx-auto mb-2" data-id="element-387" />
                    <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-388">
                      {mockServer.uptime}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-389">
                      Uptime (30d)
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg" data-id="element-390">
                    <ZapIcon className="h-5 w-5 text-slate-400 dark:text-slate-500 mx-auto mb-2" data-id="element-391" />
                    <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-392">
                      {mockServer.avgLatency}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-393">
                      Avg Latency
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg" data-id="element-394">
                    <ShieldCheckIcon className="h-5 w-5 text-slate-400 dark:text-slate-500 mx-auto mb-2" data-id="element-395" />
                    <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-396">
                      {mockServer.allowedTools}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-397">
                      Allowed Tools
                    </div>
                  </div>
                </div>
              </Card>

              {/* Recent Requests */}
              <Card data-id="element-398">
                <CardHeader title="Recent Requests" action={<Link to={`/audit?server=${serverId}`} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300" data-id="element-400">
                      View all
                    </Link>} data-id="element-399" />
                <AuditTimeline events={mockRecentEvents.slice(0, 5)} data-id="element-401" />
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6" data-id="element-402">
              {/* Endpoint */}
              <Card data-id="element-403">
                <CardHeader title="MCP Endpoint" data-id="element-404" />
                <div className="flex items-center gap-2" data-id="element-405">
                  <code className="flex-1 text-xs font-mono text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-1.5 rounded truncate" data-id="element-406">
                    {mockServer.endpoint}
                  </code>
                  <CopyButton value={mockServer.endpoint} data-id="element-407" />
                </div>
              </Card>

              {/* Policy Summary */}
              <PolicySummary rules={mockPolicyRules} mode="default-deny" data-id="element-408" />
            </div>
          </div>}

        {activeTab === 'policy' && <div className="text-center py-12" data-id="element-409">
            <p className="text-slate-500 dark:text-slate-400 mb-4" data-id="element-410">
              Policy configuration is available in the Policy Builder
            </p>
            <Link to={`/servers/${serverId}/policy`} data-id="element-411">
              <Button variant="primary" data-id="element-412">Open Policy Builder</Button>
            </Link>
          </div>}

        {activeTab === 'audit' && <Card data-id="element-413">
            <CardHeader title="Audit Log" description="All requests processed by this server" action={<Link to={`/audit?server=${serverId}`} data-id="element-415">
                  <Button variant="secondary" size="sm" data-id="element-416">
                    View Full Audit Inbox
                  </Button>
                </Link>} data-id="element-414" />
            <AuditTimeline events={mockRecentEvents} data-id="element-417" />
          </Card>}

        {activeTab === 'runtime' && <Card data-id="element-418">
            <CardHeader title="Runtime Configuration" data-id="element-419" />
            <dl className="grid grid-cols-2 gap-4 text-sm" data-id="element-420">
              <dt className="text-slate-500 dark:text-slate-400" data-id="element-421">
                CPU Allocation
              </dt>
              <dd className="text-slate-900 dark:text-slate-100" data-id="element-422">0.5 vCPU</dd>
              <dt className="text-slate-500 dark:text-slate-400" data-id="element-423">Memory</dt>
              <dd className="text-slate-900 dark:text-slate-100" data-id="element-424">512 MB</dd>
              <dt className="text-slate-500 dark:text-slate-400" data-id="element-425">
                Execution Timeout
              </dt>
              <dd className="text-slate-900 dark:text-slate-100" data-id="element-426">30 seconds</dd>
              <dt className="text-slate-500 dark:text-slate-400" data-id="element-427">
                Network Egress
              </dt>
              <dd data-id="element-428">
                <Badge variant="healthy" data-id="element-429">Disabled</Badge>
              </dd>
              <dt className="text-slate-500 dark:text-slate-400" data-id="element-430">
                Filesystem Mode
              </dt>
              <dd className="text-slate-900 dark:text-slate-100" data-id="element-431">Read-only</dd>
            </dl>
          </Card>}
      </div>
    </div>;
}